import { useState } from "react";
import { toast } from "react-toastify";

import useCampaignFlow from "../datahooks/useCampaignFlow";

import type {
  CampaignFlow,
  FlowInmailTemplate,
  FlowNoteTemplate,
  FlowTemplate,
  ReactionType,
} from "../types";

export default function useFlowActions(flow: CampaignFlow) {
  const [actionErrors, setActionErrors] = useState({ react: undefined });
  const { addEmptyFlowTemplate, updateFlow } = useCampaignFlow();

  function setFlowLanguage(languageCode: string, isManual: boolean) {
    updateFlow({
      flowId: flow.id,
      updates: {
        source_language: languageCode,
        is_manual_language: isManual,
      },
    });
  }

  function setTranslate(isTranslateEnabled: boolean) {
    updateFlow({
      flowId: flow.id,
      updates: {
        translate: isTranslateEnabled,
      },
    });
  }

  function updateFlowTemplate(flowTemplate: Partial<FlowTemplate>) {
    updateFlow({
      flowId: flow.id,
      updates: {
        template: flowTemplate as FlowInmailTemplate | FlowNoteTemplate,
      },
    });
  }

  function setEndorseSettings(isEnabled: boolean, delay: number) {
    updateFlow({
      flowId: flow.id,
      updates: {
        endorse_delay: delay,
        endorse_enabled: isEnabled,
      },
    });
  }

  function setReactSettings(
    isEnabled: boolean,
    type: ReactionType,
    delay: number,
  ) {
    setActionErrors({ react: undefined });

    if (delay > flow.withdraw_delay) {
      setActionErrors({
        react: "React delay cannot be longer than withdrawal delay",
      });
    } else {
      updateFlow({
        flowId: flow.id,
        updates: {
          like_enabled: isEnabled,
          like_type: type,
          like_delay: delay,
        },
      });
    }
  }

  function setWithdrawDelay(unixMs: number) {
    if (flow.like_enabled && unixMs < flow.like_delay) {
      toast.error("Withdraw can't be less than React delay");
    } else {
      updateFlow({
        flowId: flow.id,
        updates: {
          withdraw_delay: unixMs,
        },
      });
    }
  }

  function addEmptyTemplate() {
    addEmptyFlowTemplate(flow.id);
  }

  return {
    actionErrors,
    addEmptyTemplate,
    setFlowLanguage,
    setTranslate,
    updateFlowTemplate,
    setWithdrawDelay,
    setEndorseSettings,
    setReactSettings,
  };
}
